import * as React from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";

import {
  AGENT_SUBJECTS,
  implicitSubjectOfVariableName,
  type TemplateVariable,
} from "@/shared/domain/agents";
import { FormBlock, TextInput } from "@/shared/foundation/primitives";

import {
  IMPLICIT_COLLECTION_DESCRIPTION,
  IMPLICIT_COLLECTION_VAR_NAME,
  type VariableMeta,
} from "../Agents/buildRequestTemplate";

import { type AgentFormValues } from "./agentFormSchema";
import { KeyValueTable } from "./KeyValueTable";
import { useAgentVariableCatalog } from "./useAgentVariableCatalog";
import styles from "./VariablesBlock.module.css";

const BLANK: VariableMeta = { description: "", default: "" };

/**
 * What a row the person cannot edit says, or nothing for a row they can: a
 * kept declaration's own description, or the fixed meaning of a variable
 * nobody typed.
 */
function fixedDescription(
  name: string,
  kept: TemplateVariable | undefined,
  agentSubject: AgentFormValues["subject"],
): string | null {
  if (kept !== undefined) {
    return kept.description || "Set through the SDK or API.";
  }
  const subject = implicitSubjectOfVariableName(name, agentSubject);
  if (subject !== undefined) {
    return AGENT_SUBJECTS[subject].variableDescription;
  }
  if (name === IMPLICIT_COLLECTION_VAR_NAME) {
    return IMPLICIT_COLLECTION_DESCRIPTION;
  }
  return null;
}

/**
 * Every variable the agent declares, with what to tell the person launching
 * it.
 *
 * The names are not typed here — they are read out of the text that mentions
 * them — so a row appears the moment a `{{name}}` does. Some arrive without
 * being typed at all: the dataset or session the agent runs on (the other
 * one's `dataset.id` or `session.id` is an ordinary variable), and the
 * collection a create-events goal files into. Their meanings are invariant, so
 * those rows state theirs instead of asking. A declaration the editor keeps as
 * saved (see `passthroughVariables`) is read-only too, since a save writes it
 * back unchanged.
 *
 * A default is not asked for here. One a saved agent already carries stays in
 * the record untouched, so editing the agent does not drop it.
 *
 * The whole record is held by one `Controller` rather than a field per row,
 * because a variable's name is the record's key and may carry a dot (every
 * implicit one does), which react-hook-form would read as another level of
 * nesting.
 */
export function VariablesBlock() {
  const { control } = useFormContext<AgentFormValues>();
  const catalog = useAgentVariableCatalog();
  const passthroughVariables = useWatch({
    control,
    name: "passthroughVariables",
  });
  const subject = useWatch({ control, name: "subject" });
  const keptByName = new Map(
    passthroughVariables.map((variable) => [variable.name, variable]),
  );
  const titleId = React.useId();
  const descriptionHeadingId = React.useId();
  const names = catalog.paths.map((entry) => entry.path);

  return (
    <FormBlock
      titleId={titleId}
      title="Variables"
      className={styles.block}
      data-testid="agent-variables"
      data-count={names.length}
    >
      <Controller
        control={control}
        name="variableMeta"
        render={({ field }) => {
          const meta = field.value;
          const write = (name: string, patch: Partial<VariableMeta>) => {
            field.onChange({
              ...meta,
              [name]: { ...BLANK, ...meta[name], ...patch },
            });
          };
          return (
            <KeyValueTable
              keyHeading="Name"
              valueHeading="Description"
              valueHeadingId={descriptionHeadingId}
            >
              {names.length === 0 ? (
                <KeyValueTable.EmptyRow data-testid="agent-variables-empty">
                  None. Add a <code>{"{{name}}"}</code> to the message to ask
                  for a value at launch.
                </KeyValueTable.EmptyRow>
              ) : (
                names.map((name) => {
                  const fixed = fixedDescription(
                    name,
                    keptByName.get(name),
                    subject,
                  );
                  return (
                    <KeyValueTable.Row
                      key={name}
                      label={name}
                      labelClassName={styles.name}
                      data-testid={`agent-variable-${name}`}
                    >
                      {fixed === null ? (
                        <TextInput
                          aria-labelledby={descriptionHeadingId}
                          placeholder="What this is for, shown to whoever launches the agent."
                          value={meta[name]?.description ?? ""}
                          onChange={(event) => {
                            write(name, {
                              description: event.target.value,
                            });
                          }}
                        />
                      ) : (
                        <p className={styles.fixed}>{fixed}</p>
                      )}
                    </KeyValueTable.Row>
                  );
                })
              )}
            </KeyValueTable>
          );
        }}
      />
    </FormBlock>
  );
}
